/**
 * Battle-AI (v0M3.8a, revised v0M3.5+fix2).
 *
 * Runs at the start of every round in `advanceBattleRound`. Ensures
 * every non-broken cohort always has SOMETHING to do so battles can
 * never stall for lack of orders.
 *
 * Post-v0M3.5 revision: no longer gated by `Battle.autoAI`. The autoAI
 * flag now indicates only whether the player is currently commanding
 * (UI state), not whether the AI should engage. Instead:
 *
 *  1. Skip broken cohorts.
 *  2. Skip `standingOrder === 'hold-and-defend'` (clear stale moveOrder).
 *  3. If cohort already has a `moveOrder` (player-issued, or set by a
 *     previous round of this same function), **keep it** — the cohort
 *     is en route. `phaseMovement` will clear the order on arrival at
 *     the target square, at which point next round's phaseBattleAI
 *     picks a fresh target.
 *  4. Otherwise: pick the nearest non-broken enemy cohort by Chebyshev
 *     distance. Ties broken by lower cohort id.
 *  5. If no enemy exists (all broken), clear moveOrder + let
 *     checkBattleEnd fire.
 *  6. If cohort is Ranged AND target is in missileRange → hold and fire
 *     (moveOrder null, phaseCombat handles).
 *  7. If cohort is Melee AND already cardinally adjacent → hold and
 *     fight (moveOrder null, phaseCombat handles).
 *  8. Otherwise set moveOrder to target's position. phaseMovement steps
 *     one cardinal square toward it per round.
 *
 * This keeps battles progressing regardless of player command state,
 * while still respecting explicit player orders for the duration of
 * their arrival at the ordered square.
 */

import type { Battle, CohortOnField, Side, Square } from './battle-state';
import { BATTLESCAPE_HEIGHT, BATTLESCAPE_WIDTH } from './battle-state';
import type { CohortId } from './state';
import { chebyshevDistance, isCombatAdjacent, isRanged } from './cohort';

export function phaseBattleAI(battle: Battle): void {
  for (let sideIdx = 0; sideIdx < battle.sides.length; sideIdx++) {
    const side = battle.sides[sideIdx]!;
    const enemySides = battle.sides.filter((_, i) => i !== sideIdx);
    const cohorts = sortedCohorts(side);
    for (const cohort of cohorts) {
      if (cohort.broken) continue;
      if (cohort.exited) continue; // v0M4.0b.2: exited cohorts no longer act.

      // v0M4.0b.2: order-specific dispatch.

      // Hold orders — clear any stale moveOrder; phase-code respects the hold.
      if (cohort.standingOrder === 'hold-and-defend' || cohort.standingOrder === 'hold-and-delay') {
        cohort.moveOrder = null;
        continue;
      }

      // Withdraw with Haste — override moveOrder to the nearest edge cell.
      // Never overwritten by later logic; recomputed each round so a moving
      // cohort continues tracking the closest edge as it advances.
      if (cohort.standingOrder === 'withdraw-with-haste') {
        cohort.moveOrder = { targetSquare: nearestEdgeCell(cohort.position) };
        continue;
      }

      // v0M4.0d Orderly Retreat — cohort moves toward its own side's
      // rear edge (opposite of the enemy) 1 square/round, keeping facing.
      // Distinct from Withdraw with Haste in that phaseCombat still runs
      // (cohort engages pursuers per §2.8.2). Movement is handled in
      // phaseMovement's orderly-retreat branch; here we just clear any
      // stale moveOrder so phaseMovement's dispatch takes over.
      if (cohort.standingOrder === 'orderly-retreat') {
        cohort.moveOrder = null;
        continue;
      }

      // Respect existing moveOrder — cohort is en route (player-set or
      // AI-set previously). phaseMovement clears the order on arrival.
      // Never overwrite a live order; that's the "sticky" semantic that
      // lets player-issued targets survive until the cohort arrives.
      if (cohort.moveOrder !== null) continue;

      // v0M4.0b.2: target-selection preference for flank-melee / flank-ranged
      // (§2.8.1.3). Filter enemies by tag; fall through to any-enemy if
      // no target of preferred type exists. Real "route to a flanking
      // cell" logic lands at v0M4.0e movement extension; b.2 ships the
      // target-selection half only.
      const preferredTag: 'Melee' | 'Ranged' | null =
        cohort.standingOrder === 'flank-melee' ? 'Melee'
          : cohort.standingOrder === 'flank-ranged' ? 'Ranged'
          : null;

      const target = pickNearestEnemyWithPreference(cohort, enemySides, preferredTag);
      if (target === null) {
        // No enemies to engage — nothing to do this round; checkBattleEnd
        // will fire at the tail of the round.
        continue;
      }

      const dist = chebyshevDistance(cohort.position, target.position);

      if (isRanged(cohort) && dist <= cohort.stats.missileRange) {
        // Ranged in range — stand and fire; phaseCombat handles.
        continue;
      }
      if (isCombatAdjacent(cohort.position, target.position)) {
        // Melee already engaged — hold; phaseCombat handles.
        continue;
      }
      cohort.moveOrder = { targetSquare: target.position };
    }
  }
}

/**
 * Compute the nearest edge cell to `from`. Returns the coordinate on the
 * closest edge (N / S / E / W). Ties broken deterministically: N > S > W > E
 * (top-left bias). v0M4.0b.2 helper for Withdraw with Haste per §2.8.1.1.
 */
function nearestEdgeCell(from: Square): Square {
  const dN = from.y;
  const dS = BATTLESCAPE_HEIGHT - 1 - from.y;
  const dW = from.x;
  const dE = BATTLESCAPE_WIDTH - 1 - from.x;
  const minD = Math.min(dN, dS, dW, dE);
  if (minD === dN) return { x: from.x, y: 0 };
  if (minD === dS) return { x: from.x, y: BATTLESCAPE_HEIGHT - 1 };
  if (minD === dW) return { x: 0, y: from.y };
  return { x: BATTLESCAPE_WIDTH - 1, y: from.y };
}

function sortedCohorts(side: Side): CohortOnField[] {
  const ids = [...side.cohorts.keys()].sort((a, b) => (a as number) - (b as number)) as CohortId[];
  return ids.map((id) => side.cohorts.get(id)!);
}

/**
 * Nearest-enemy pick with optional tag preference (v0M4.0b.2).
 *
 * `preferredTag`:
 *  - `null` — pick nearest of any enemy (original behaviour).
 *  - `'Melee'` — pick nearest enemy with Melee tag; if none, fall through
 *    to nearest of any (flank-melee target selection per §2.8.1.3).
 *  - `'Ranged'` — same but Ranged tag (flank-ranged).
 *
 * Deterministic: ties on distance break to lower cohort id.
 */
function pickNearestEnemyWithPreference(
  cohort: CohortOnField,
  enemySides: readonly Side[],
  preferredTag: 'Melee' | 'Ranged' | null,
): CohortOnField | null {
  if (preferredTag !== null) {
    const preferred = pickNearestEnemyMatching(cohort, enemySides, (e) => e.tags.includes(preferredTag));
    if (preferred !== null) return preferred;
    // Fall through to any-enemy when no preferred-tag target exists.
  }
  return pickNearestEnemyMatching(cohort, enemySides, () => true);
}

function pickNearestEnemyMatching(
  cohort: CohortOnField,
  enemySides: readonly Side[],
  predicate: (enemy: CohortOnField) => boolean,
): CohortOnField | null {
  let best: CohortOnField | null = null;
  let bestDist = Infinity;
  for (const side of enemySides) {
    const enemyIds = [...side.cohorts.keys()].sort((a, b) => (a as number) - (b as number));
    for (const eid of enemyIds) {
      const enemy = side.cohorts.get(eid as CohortId)!;
      if (enemy.broken) continue;
      if (!predicate(enemy)) continue;
      const d = chebyshevDistance(cohort.position, enemy.position);
      if (d < bestDist) {
        bestDist = d;
        best = enemy;
      }
    }
  }
  return best;
}
